import { Box, Flex, Spinner } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import NoItemsFound from '../../../components/no-items-found/NoItemsFound';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import { getProviderAbsences } from '../../../services/absences/providerAbsenceService';
import { ProviderAbsence } from '../../../types/absences/types';

interface Provider {
  id: string;
  firstname?: string;
  middlename?: string;
  lastname?: string;
  suffix?: string;
  name?: string; // Optional name field for provider
}

interface AbsencesTableProps {
  practiceId: string;
  providerIds: string[];
  providers: Provider[]; // Updated type for providers
}

const AbsencesTable: React.FC<AbsencesTableProps> = ({
  practiceId,
  providerIds,
  providers,
}) => {
  const [absences, setAbsences] = useState<ProviderAbsence[] | null>(
    null
  );
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [errorAbsences, setErrorAbsences] = useState<string | null>(
    null
  );

  useEffect(() => {
    const fetchAbsences = async () => {
      if (!practiceId || !providerIds.length) {
        setErrorAbsences(
          'Practice ID or Provider IDs are missing. Unable to fetch absences.'
        );
        return;
      }

      setLoadingAbsences(true);
      try {
        const response = await getProviderAbsences({
          practiceIds: [practiceId],
          providerIds,
        });

        if (response.absences) {
          setAbsences(response.absences as ProviderAbsence[]);
        } else {
          throw new Error(
            (response as { message?: string }).message ||
              'Failed to fetch absences.'
          );
        }
      } catch (error) {
        console.error('Error fetching provider absences:', error);
        setErrorAbsences(
          typeof error === 'object' &&
            error !== null &&
            'message' in error
            ? (error as { message: string }).message
            : 'Failed to load provider absences.'
        );
      } finally {
        setLoadingAbsences(false);
      }
    };

    fetchAbsences();
  }, [practiceId, providerIds]);

  const columns = [
    { header: 'Provider Name', accessor: 'providerName' }, // Updated column header
    { header: 'Start Date', accessor: 'startDate' },
    { header: 'End Date', accessor: 'endDate' },
  ];

  const tableData = absences?.map((absence) => {
    const provider = providers.find(
      (p) => p.id === absence.providerId
    );

   const providerName = provider
      ? provider.name // Use `name` if it exists
        ? provider.name
        : [
            provider.lastname,
            provider.firstname,
            provider.middlename,
            provider.suffix,
          ]
            .filter(Boolean)
            .join(', ')
      : '-';

    return {
      ...absence,
      providerName,
    };
  });

  return (
    <Box>
      {loadingAbsences ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <Spinner size="lg" />
        </Flex>
      ) : errorAbsences ? (
        <NoItemsFound
          mainMessage="Error Fetching Absences"
          detailMessage={errorAbsences}
          refetch={() =>
            getProviderAbsences({
              practiceIds: [practiceId],
              providerIds,
            })
          }
        />
      ) : absences?.length === 0 ? (
        <NoItemsFound
          mainMessage="No Absences Found"
          detailMessage="There are no absences available for the selected practice and providers."
          refetch={() =>
            getProviderAbsences({
              practiceIds: [practiceId],
              providerIds,
            })
          }
        />
      ) : (
        <ResourceGrid columns={columns} data={tableData ?? []} />
      )}
    </Box>
  );
};

export default AbsencesTable;